// Copyright (c) 2024 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import * as Actions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executorMutation, isConnectError, timelineService } from "../../../app/rpc"
import { MergeTimeGroupsRequestSchema, TimeGroup } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

type MergeTimeGroupRpc = (payload: Actions.MergeTimeGroupsPayload) => Observable<TimeGroup>

export const mergeTimeGroups = (mergeTimeGroupsRpc: MergeTimeGroupRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.mergeTimeGroups.match),
      mergeMap(action => of(action.payload)
        .pipe(
          mergeMap(mergeTimeGroupsRpc),
          map(timeGroup => timeGroup),
          mergeMap((timeGroup) => concat(
            of(Actions.mergeTimeGroupsFulfilled()),
            of(TimelineSlice.deleteTimeGroups(action.payload.request.timeGroupIds ?? [])),
            of(TimelineSlice.upsertTimeGroup(timeGroup)),
            of(TimelineSlice.setTimeRowsGroupId({
              timeRowIds: action.payload.timeRowIds,
              timeGroupId: timeGroup.id,
            })),
            of(Actions.generateTimesheetFieldConfigs([timeGroup])),
            of(TimelineSlice.replaceActiveTimeGroupsIds({ timeGroupsIds: [timeGroup.id], })),
          )),
          catchError(handleError),
        )
      ),
    )

const handleError = (e: unknown): Observable<Action> => {
  const actions: Action[] = [
    Actions.mergeTimeGroupsErrored(),
  ]
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.FailedPrecondition) {
      actions.push(
        showSnackBar({
          message: t({ id: "msg_merge_time_groups_failed_precondition", message: "Failed to merge timesheet entries. Timesheet entries may be out of sync." }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
    if (code === Code.PermissionDenied) {
      actions.push(showSnackBar({
        message: t({ id: "msg_merge_time_groups_permission_denied", message: "Failed to merge timesheet entries. You do not have permission." }),
        messageType: "ERROR",
      }))
      return concat(actions)
    }
  }
  actions.push(showSnackBar({
    message: t({ id: "msg_merge_time_groups_failed", message: "Failed to merge timesheet entries. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
  return concat(actions)
}

const mergeTimeGroupsRpc = (payload: Actions.MergeTimeGroupsPayload): Observable<TimeGroup> =>
  executorMutation(() => timelineService.mergeTimeGroups(create(MergeTimeGroupsRequestSchema, payload.request)))

export default mergeTimeGroups(mergeTimeGroupsRpc)
